import { useSelector } from "react-redux";
import { pbxIdSelector } from "@/react/features/customer/customerSlice";
import { CustomerCard } from "@/react/features/customer/card";
import React, { useCallback } from "react";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import { useTable } from "@/react/components/table/useTable";
import schema from "./schema";
import columnDefinition from "./columnDefinition";
import {
  handleCreateResponse,
  handleDeleteResponse,
  handleUpdateResponse,
  useCreateRingGroupsMutation,
  useDeleteRingGroupsMutation,
  useGetRingGroupsQuery,
  useUpdateRingGroupsMutation,
} from "./ringGroupsApi";
import TableComponent from "@/react/components/table/TableComponent";
import { appendPropertiesToMembers } from "@/react/features/smp/appendPropertiesToMembers";
import PageLayout from "@/react/features/styled/PageLayout";
import ContentWrapper from "@/react/features/styled/PageContentContainer";
import TableWrapper from "@/react/features/styled/TableWrapper";
import { usePrimaryDidAndBranchIds } from "@/react/features/smp/usePrimaryDidAndBranchIds";
import PageTitle from "@/react/components/PageTitle";

const columns = columnDefinition();

const memberAttributesFromRingGroup = {
  ringGroupSubscriptionId: "subscriptionId",
  customerId: "customerId",
};

const RingGroupsPage = () => {
  const pbxId = useSelector(pbxIdSelector);

  const {
    data: ringGroups,
    isFetching,
    error,
    refetch,
  } = useGetRingGroupsQuery(pbxId);

  const [createRingGroups, { isLoading: isCreating }] =
    useCreateRingGroupsMutation();
  const [updateRingGroups, { isLoading: isUpdating }] =
    useUpdateRingGroupsMutation();
  const [deleteRingGroups, { isLoading: isDeleting }] =
    useDeleteRingGroupsMutation();

  useErrorNotification(error);

  const { isFetchingDids, isFetchingBranches, dids, branches } =
    usePrimaryDidAndBranchIds();

  const { table, filters, state, mutations } = useTable(
    ringGroups,
    columns,
    schema,
    schema.fields.subscriptionId.spec.key
  );

  const {
    data,
    rowsToCreate,
    rowsToUpdate,
    rowsToDelete,
    resetData,

    setData,
    setRowsToCreate,
    setRowsToUpdate,
    setRowsToDelete,
  } = mutations;

  const isDirty =
    rowsToCreate.size > 0 || rowsToUpdate.size > 0 || rowsToDelete.size > 0;

  const onSave = useCallback(() => {
    if (rowsToCreate.size > 0) {
      createRingGroups({
        pbxId,
        ringGroupList: appendPropertiesToMembers(
          data,
          rowsToCreate,
          memberAttributesFromRingGroup
        ).map((ringGroup) => ({
          ...ringGroup,
          primaryDidId: dids[ringGroup.primaryDidNumber],
          branchId: branches[ringGroup.branchName],
        })),
      })
        .unwrap()
        .then(handleCreateResponse({ rowsToCreate, setRowsToCreate, setData }))
        .catch(errorNotification);
    }

    if (rowsToUpdate.size > 0) {
      updateRingGroups({
        pbxId,
        ringGroupList: appendPropertiesToMembers(
          data,
          rowsToUpdate,
          memberAttributesFromRingGroup
        ).map((ringGroup) => ({
          ...ringGroup,
          branchId: branches[ringGroup.branchName],
        })),
      })
        .unwrap()
        .then(handleUpdateResponse({ rowsToUpdate, setRowsToUpdate, setData }))
        .catch(errorNotification);
    }

    if (rowsToDelete.size > 0) {
      deleteRingGroups({
        pbxId,
        ringGroupIds: Array.from(rowsToDelete.keys()),
      })
        .unwrap()
        .then(handleDeleteResponse({ rowsToDelete, setRowsToDelete, setData }))
        .catch(errorNotification);
    }
  }, [rowsToCreate, rowsToUpdate, rowsToDelete]);

  const isTableLoading =
    isFetching ||
    isCreating ||
    isUpdating ||
    isDeleting ||
    isFetchingDids ||
    isFetchingBranches;

  return (
    <PageLayout>
      <CustomerCard hasPbxSelector={true} />
      <ContentWrapper>
        <PageTitle>Ring Groups</PageTitle>
        <TableWrapper>
          <TableComponent
            title="Ring Groups"
            schema={schema}
            isDirty={isDirty}
            isLoading={isTableLoading}
            onSave={onSave}
            onCancel={resetData}
            onRefresh={refetch}
            table={table}
            filters={filters}
            mutations={mutations}
            state={state}
          />
        </TableWrapper>
      </ContentWrapper>
    </PageLayout>
  );
};

export default RingGroupsPage;
